/**
 * api-config — the single explicit API-target resolver (APP-W06a).
 *
 * Objective: no artifact silently targets localhost, the wrong environment or
 * a credentialed preview API. There is no implicit default and no other
 * fallback anywhere: an authenticating build must declare VITE_APP_ENV and an
 * explicit, validated VITE_PUBLIC_API_V2_URL, or the app fails closed to the
 * non-authenticating screen (see src/routes/__root.tsx).
 *
 * Public inputs (all optional, all plain values — never secrets):
 *   VITE_APP_ENV           local | integration | staging | production | preview
 *                          (absent defaults to 'local')
 *   VITE_TALI_API_MODE     legacy-readonly | durable-r2
 *                          (absent defaults to 'legacy-readonly';
 *                          durable-r2 is recognised but INACTIVE — it is never
 *                          silently downgraded onto the legacy adapter)
 *   VITE_PUBLIC_API_V2_URL explicit API base origin, required for every
 *                          authenticating environment
 *
 * Approved release origins for staging/production are supplied by the later
 * backend/operator manifest — they are NEVER inferred from app hostnames and
 * are not hardcoded here. Until that manifest is bound, release targets fail
 * closed as 'unapproved-release-target'.
 *
 * This module is pure with respect to its inputs: the parser touches no
 * filesystem, no network and no ambient state beyond the single import-time
 * `apiConfig` singleton built from import.meta.env below. Tests drive the
 * pure `resolveApiConfig` with explicit synthetic values only.
 */

export const APP_ENV_VALUES = [
  'local',
  'integration',
  'staging',
  'production',
  'preview',
] as const;
export type AppEnv = (typeof APP_ENV_VALUES)[number];

export const API_MODE_VALUES = ['legacy-readonly', 'durable-r2'] as const;
export type ApiMode = (typeof API_MODE_VALUES)[number];

/**
 * Stable, secret-free failure identifiers. Reasons never embed the raw
 * configured value, so a mistyped credential can never be echoed back.
 */
export type ConfigUnavailableReason =
  | 'unknown-app-env'
  | 'unknown-api-mode'
  | 'missing-target'
  | 'unparsable-target'
  | 'invalid-scheme'
  | 'target-with-credentials'
  | 'target-with-fragment'
  | 'loopback-release-target'
  | 'unapproved-release-target'
  | 'inactive-api-mode';

export type ApiConfigResolution =
  | {
      status: 'ok';
      env: AppEnv;
      mode: ApiMode;
      /** Exact trimmed target as supplied; empty string when none is used. */
      apiOrigin: string;
      /** True only for environments permitted to issue credentialed requests. */
      authenticating: boolean;
    }
  | {
      status: 'unavailable';
      env: AppEnv | 'unknown';
      mode: ApiMode | 'unknown';
      reason: ConfigUnavailableReason;
    };

export interface ApiConfigEnvInput {
  /** VITE_APP_ENV */
  appEnv?: string;
  /** VITE_TALI_API_MODE */
  apiMode?: string;
  /** VITE_PUBLIC_API_V2_URL */
  apiUrl?: string;
  /**
   * Approved release origins for staging/production, from the later
   * backend/operator manifest. Absent (the current state) means no release
   * target can be approved yet.
   */
  approvedReleaseOrigins?: readonly string[];
}

function parseAppEnv(raw: string | undefined): AppEnv | 'unknown' {
  const value = (raw ?? '').trim();
  if (value === '') return 'local'; // documented default
  return (APP_ENV_VALUES as readonly string[]).includes(value)
    ? (value as AppEnv)
    : 'unknown';
}

function parseApiMode(raw: string | undefined): ApiMode | 'unknown' {
  const value = (raw ?? '').trim();
  if (value === '') return 'legacy-readonly'; // documented default
  return (API_MODE_VALUES as readonly string[]).includes(value)
    ? (value as ApiMode)
    : 'unknown';
}

function isLoopbackHost(hostname: string): boolean {
  // URL.hostname keeps IPv6 literals bracketed (e.g. '[::1]'); strip them.
  const host = hostname.toLowerCase();
  const bare = host.startsWith('[') && host.endsWith(']') ? host.slice(1, -1) : host;
  if (bare === 'localhost' || bare.endsWith('.localhost')) return true;
  if (bare === '::1' || bare === '::') return true;
  const ipv4 = bare.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (ipv4) return ipv4[1] === '127';
  return false;
}

type TargetFailure = Exclude<ConfigUnavailableReason, 'unknown-app-env' | 'unknown-api-mode' | 'inactive-api-mode'>;

function validateApiTarget(
  raw: string | undefined,
  env: AppEnv,
  approvedReleaseOrigins: readonly string[] | undefined
): { ok: true; origin: string } | { ok: false; reason: TargetFailure } {
  const value = (raw ?? '').trim();
  if (value === '') return { ok: false, reason: 'missing-target' };

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { ok: false, reason: 'unparsable-target' };
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { ok: false, reason: 'invalid-scheme' };
  }
  // Userinfo in the target would place credentials into every built URL.
  if (url.username !== '' || url.password !== '') {
    return { ok: false, reason: 'target-with-credentials' };
  }
  // Fragments never reach the network and signal a malformed target.
  if (url.hash !== '') {
    return { ok: false, reason: 'target-with-fragment' };
  }

  if (isLoopbackHost(url.hostname)) {
    // Loopback is a local/integration concept only; a release build must
    // never point at loopback ("loopback release").
    if (env === 'staging' || env === 'production') {
      return { ok: false, reason: 'loopback-release-target' };
    }
  }

  if (env === 'staging' || env === 'production') {
    const approved = new Set((approvedReleaseOrigins ?? []).map((o) => o.trim()).filter(Boolean));
    if (!approved.has(url.origin)) {
      return { ok: false, reason: 'unapproved-release-target' };
    }
  }

  // The exact trimmed supplied value is preserved (compatible with the
  // historical raw-base behaviour); validation is performed on the parsed URL.
  return { ok: true, origin: value };
}

/**
 * Pure resolver. Same-shape inputs always produce the same result; no
 * filesystem, network or ambient state is consulted.
 */
export function resolveApiConfig(input: ApiConfigEnvInput): ApiConfigResolution {
  const env = parseAppEnv(input.appEnv);
  if (env === 'unknown') {
    return { status: 'unavailable', env: 'unknown', mode: 'unknown', reason: 'unknown-app-env' };
  }

  const mode = parseApiMode(input.apiMode);
  if (mode === 'unknown') {
    return { status: 'unavailable', env, mode: 'unknown', reason: 'unknown-api-mode' };
  }

  // Declared durable mode stays inactive until its adapters are shipped and
  // accepted. It is never a silent legacy mutation fallback.
  if (mode === 'durable-r2') {
    return { status: 'unavailable', env, mode, reason: 'inactive-api-mode' };
  }

  // Public preview never authenticates and makes no credentialed request:
  // it resolves no API target at all, so a preview deployment cannot leak
  // credentials to any API origin.
  if (env === 'preview') {
    return { status: 'ok', env, mode, apiOrigin: '', authenticating: false };
  }

  const target = validateApiTarget(input.apiUrl, env, input.approvedReleaseOrigins);
  if (!target.ok) {
    return { status: 'unavailable', env, mode, reason: target.reason };
  }

  return { status: 'ok', env, mode, apiOrigin: target.origin, authenticating: true };
}

/**
 * Import-time singleton. Read only from import.meta.env (never from
 * filesystem or process state) with no approved release manifest bound yet.
 * A non-authenticating result leaves the app gated to the neutral screen.
 */
export const apiConfig: ApiConfigResolution = resolveApiConfig({
  appEnv: import.meta.env.VITE_APP_ENV,
  apiMode: import.meta.env.VITE_TALI_API_MODE,
  apiUrl: import.meta.env.VITE_PUBLIC_API_V2_URL,
  approvedReleaseOrigins: undefined,
});

/** True only when credentialed API requests are permitted in this build. */
export function isAuthenticating(): boolean {
  return apiConfig.status === 'ok' && apiConfig.authenticating;
}

/**
 * The exact API base for authenticating builds, or null when this build may
 * not issue credentialed requests. Null — never a guessed host.
 */
export function getAuthenticatingApiOrigin(): string | null {
  return isAuthenticating() ? (apiConfig as { apiOrigin: string }).apiOrigin : null;
}
